import React, { useEffect, useRef, useState } from "react";
import TaskView from "../common/TaskView";
import { AcousticTask, AcousticTaskDefinition } from "../common/models";
import { randomUUID } from "../../../utils";
import AudioLevelDisplay from "../../../audio/AudioLevelDisplay";
import Icon from "@mdi/react";
import { mdiNavigation } from "@mdi/js";
import './Loudness.css';
import { translateReactNode } from "../../../../utils/translateReactNode";

const maxTaskDurationTillSuccess: number = 30
const successDecibelThreshold: number = -6
const decibelsThresholdToAddToMaxBuffer: number = -18

enum TryOptions {
  None = 0,
  FirstTry = 1,
  SecondTry = 2,
  ThirdTry = 3,
  Completed = 4
}

const LoudnessTaskEncouragementView = ({ currentTry }: { currentTry: TryOptions}) => {
  const [_scale, setScale] = useState(0);

  // Optional: Animate scale on mount or update
  useEffect(() => {
    setScale(0.05); // Simulate a slight scale effect
    const timeout = setTimeout(() => setScale(0), 300);
    return () => clearTimeout(timeout);
  }, [currentTry]);

  const getMessage = () => {
    switch (currentTry) {
      case TryOptions.SecondTry:
        return 'Great effort! This time try to shout even louder';
      case TryOptions.ThirdTry:
        return 'Good job! Try again even louder';
      case TryOptions.Completed:
        return 'Good work! Now, press the button again to stop recording.';
      default:
        return null;
    }
  };

  const message = getMessage();

  if (!message) return null;

  return (
    <div className="encouragement">
      {translateReactNode(message, "generic")}
    </div>
  );
}

const LoudnessAudioLevelDisplay = ({stream, isRecording}: {stream?: MediaStream, isRecording: boolean}) => {
  const [currentTry, setCurrentTry] = useState<number>(isRecording ? TryOptions.FirstTry : TryOptions.None);
  const currentTryRef = useRef(currentTry);
  const [firstTryMaxDecibels, setFirstTryMaxDecibels] = useState<number>(-Infinity);
  const [secondTryMaxDecibels, setSecondTryMaxDecibels] = useState<number>(-Infinity);
  const [timeElapsed, setTimeElapsed] = useState<number>(0);
  const bufferRef = useRef<number[]>([]);
  const maxBufferRef = useRef<number[]>([]);
  const startTimeRef = useRef<Date | null>(null);


  useEffect(() => {
    currentTryRef.current = currentTry;
  }, [currentTry]);

   // Cyclical buffer helpers
  const pushCyclical = (arr: number[], value: number, max: number) => {
    if (arr.length >= max) arr.shift();
    arr.push(value);
  };

  const roundToDecimalPlaces = (num: number, places: number) => {
    const multiplier = Math.pow(10, places);
    return Math.round(num * multiplier) / multiplier;
  };

  const updateCurrentTry = (timeElapsed: number, db: number) => {
    if (timeElapsed > maxTaskDurationTillSuccess) {
      setCurrentTry(TryOptions.Completed);
      return;
    }

    if (db > successDecibelThreshold) {
      setCurrentTry(TryOptions.Completed);
      return;
    }

    // Enqueue db to cyclical buffer (max 100)
    pushCyclical(bufferRef.current, db, 100);
    // Every 1s
    if (timeElapsed % 1 === 0) {
      const max = Math.max(...bufferRef.current);
      if (max > decibelsThresholdToAddToMaxBuffer) {
        pushCyclical(maxBufferRef.current, max, 10);
      }
      if (maxBufferRef.current.length === 0) return;
      // Every 2s
      if (timeElapsed % 2 === 0) {
        const maxOfMax = Math.max(...maxBufferRef.current);
        const tryValue = currentTryRef.current;

        if (tryValue === TryOptions.FirstTry) {
          setFirstTryMaxDecibels(maxOfMax);
          setCurrentTry(TryOptions.SecondTry);
        } else if (
          tryValue === TryOptions.SecondTry &&
          maxOfMax > firstTryMaxDecibels
        ) {
          setSecondTryMaxDecibels(maxOfMax);
          setCurrentTry(TryOptions.ThirdTry);
        } else if (
          tryValue === TryOptions.ThirdTry &&
          (maxOfMax > firstTryMaxDecibels || maxOfMax > secondTryMaxDecibels)
        ) {
          setCurrentTry(TryOptions.Completed);
        }
      }
      // Clear buffer for next second
      bufferRef.current = [];
    }
  }

  const handleOnChange = (db: number) => {
    if (!isRecording) {
      setCurrentTry(TryOptions.None);
      return;
    }

    const startTime = startTimeRef.current;
    if (!startTime) return;

    const currentTimeElapsed = roundToDecimalPlaces(((Date.now() - startTime.getTime()) / 1000), 2);
    if (currentTimeElapsed !== timeElapsed) {
      setTimeElapsed(currentTimeElapsed);
      updateCurrentTry(currentTimeElapsed, db);
    }
  };

  useEffect(() => {
    if (isRecording) {
      setCurrentTry(TryOptions.FirstTry);
      setFirstTryMaxDecibels(-Infinity);
      setSecondTryMaxDecibels(-Infinity);
      bufferRef.current = [];
      maxBufferRef.current = [];
      startTimeRef.current = new Date();
    } else {
      setCurrentTry(TryOptions.None);
    }
  }, [isRecording]);

  return (
    <>
      <div style={{position: 'relative', height: '3rem', margin: '1rem 0 5rem'}}>
        <div style={{position: 'absolute', top: 0, width: '100%', zIndex: 1}}>
          <AudioLevelDisplay stream={stream} onChange={handleOnChange} />
        </div>
        <div className="loudness-target" style={{position: 'absolute', right: '24.142242497081625%', marginRight: '-1.5rem', zIndex: 2}}>
          <div className="loudness-target-arrow" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="loudness-target-line" style={{ height: 50 }}></div>
            <Icon className="loudness-target-icon" path={mdiNavigation} size={'3rem'} />
          </div>
        </div>
      </div>
      <LoudnessTaskEncouragementView currentTry={currentTry} />
    </>
  );
}

const Definition: AcousticTaskDefinition = {
  name: "Loudness (v2)",
  title: "Loudness",
  description: "This helps us to determine the loudness of the voice.",
  cohort: 'generic',
  type: 'acoustic-task',
  estimatedCompletionTime: '0:25',
  subTaskDefinitions: [{
    name: "Loudness (v2)",
    title: "",
    instructions: `When you're ready, press the record button and say “hey” in your normal voice. Then, shout “hey” as loud as you can. Try to reach the target line on the screen. Follow the prompts on the screen to continue.`,
    content: <LoudnessAudioLevelDisplay isRecording={false} />,
    profileName: 'speech'
  }]
};
export const LoudnessDefinition = Definition;

export const Loudness =  ({onCompletion}: {onCompletion: () => void}) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [task, setTask] = useState<AcousticTask>(() => ({
    ...Definition,
    id: randomUUID(),
    startedAt: new Date().toISOString(),
    completedAt: null,
    status: 'pending',
    subTasks: Definition.subTaskDefinitions.map((definition) => ({
      ...definition,
      id: randomUUID(),
      instructions: translateReactNode(definition.instructions, Definition.cohort),
      title: translateReactNode(definition.title, Definition.cohort) as string
    })),
    content: <></>
  }));

  useEffect(() => {
    return () => {
      setStream(null);
    };
  }, []);

  useEffect(() => {
    if (stream && isRecording) {
      setTask(prev => {
        const updatedSubTask = prev.subTasks[0];
        updatedSubTask.content = <LoudnessAudioLevelDisplay stream={isRecording && stream ? stream : undefined} isRecording={isRecording} />;
        return {...prev};
      });
    } else {
      setTask(prev => {
        const updatedSubTask = prev.subTasks[0];
        updatedSubTask.content = <LoudnessAudioLevelDisplay stream={isRecording && stream ? stream : undefined} isRecording={isRecording} />;
        return {...prev};
      });
    }
  }, [isRecording, stream]);

  const handleStreamChange = (stream: MediaStream) => {
    console.log("Stream changed:", stream);
    setStream(stream);
  };

  const handleIsRecordingChange = (isRecording: boolean) => {
    console.log("Is recording:", isRecording);
    setIsRecording(isRecording);
  };

  const handleOnCompletion = () => {
    console.log("Audio task completed", task.title);
    onCompletion();
  }

  return (
    <div>
      <TaskView
        task={task}
        onCompletion={handleOnCompletion}
        onIsRecordingChange={handleIsRecordingChange}
        onStreamChange={handleStreamChange}
        />
    </div>
  );
}
